<template>
  <Modal ref="modalRef" width="40rem">
    <template #body>
      <template v-if="!isApplicationSent">
        <View gap="xl" direction="column">
          <View gap="md" direction="column" align="center">
            <Text variant="h2">Send application to {{ renterFirstName }}</Text>
            <Text variant="body-large" color="secondary">
              The application will be sent to {{ renterFirstName }} at the contact information below:
            </Text>

            <UserCard :user="renter.user" size="medium" />
          </View>

          <Hint icon="info-circle">
            <View gap="sm" direction="column">
              <Text variant="h5">Heads up: {{ renterFirstName }} has not toured the property yet</Text>
              <Text variant="body-medium">
                Once the application is sent, Tallo will stop reaching out to schedule a showing
              </Text>
            </View>
          </Hint>

          <Hint gap="md">
            <template #icon>
              <IconTile icon="lightbulb-02" size="small" color="yellow" />
            </template>

            <View gap="md" direction="column">
              <Text variant="body-medium">
                Prompt decisions on applications provide the best experience for renters and ensure you don't miss out
                on potential tenants. You can always reconsider if you choose to pass on an application.
              </Text>
            </View>
          </Hint>

          <WhatToExpectCard :items="whatToExpectItems" />

          <View gap="sm" direction="row" align="center">
            <Checkbox id="label-agree-to-tu-terms" v-model="isAgree" />
            <Text variant="body-medium" as="label" for="label-agree-to-tu-terms">
              <span>I agree to&nbsp;</span>
              <Link :to="transUnionTermsOfUseLink" color="accent" target="_blank">TransUnion Terms of Use</Link>
            </Text>
          </View>
        </View>
      </template>

      <template v-else>
        <SendApplicationSuccess :renter-name="renterFirstName" />
      </template>
    </template>

    <template #footer>
      <View v-if="!isApplicationSent" gap="md" :grow="1" class="actions">
        <Button variant="secondary" @click="close">Cancel</Button>
        <Button variant="primary" :disabled="!isAgree" :loading="isSendingApplication" @click="sendApplication">
          Send application
        </Button>
      </View>

      <Button v-else variant="primary" @click="close">Done</Button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import {
  Button,
  Checkbox,
  Hint,
  IconTile,
  Link,
  Modal,
  Text,
  View,
  useAlertsStore,
  WhatToExpectCard,
  WhatToExpectItem,
} from '@tallo/design-system';
import { PropertyInquiryDto } from '@tallo/investor/property';
import { transUnionTermsOfUseLink } from '@tallo/navigation';
import { RenterDto } from '@tallo/renter';
import { UserCard } from '@tallo/user';

import { useRenterProfileStore } from '../../store/renter-profile.store';
import SendApplicationSuccess from './send-application-success.vue';

const props = defineProps<{ propertyInquiry: PropertyInquiryDto; renter: RenterDto; isShowingHappened: boolean }>();

const alertsStore = useAlertsStore();
const renterProfileStore = useRenterProfileStore();
const modalRef = ref<InstanceType<typeof Modal>>();
const isApplicationSent = ref(false);
const isSendingApplication = ref(false);
const isAgree = ref(false);

const renterName = computed(() => props.renter.user.name);
const renterFirstName = computed(() => renterName.value.split(' ')[0]);

const whatToExpectItems = computed<WhatToExpectItem[]>(() => [
  {
    icon: 'link-01',
    title: `Tallo will send ${renterFirstName.value} an application link`,
    description: 'Tallo has partnered with TransUnion to securely handle the process.',
  },
  {
    icon: 'file-check-02',
    title: `Tallo will guide ${renterFirstName.value} through the process`,
    description: `There's no charge to you. ${renterFirstName.value} can invite co-applicants if necessary. Once completed by all parties, the application will be ready for your review.`,
  },
  {
    icon: 'folder',
    title: 'You will receive a complete application packet',
    description: `The packet includes credit, criminal, and eviction reports, along with ${renterFirstName.value}'s employment history, landlord references, and any other required documents.`,
  },
]);

function sendApplication() {
  isSendingApplication.value = true;

  renterProfileStore
    .sendApplication(props.propertyInquiry.id)
    .then(() => (isApplicationSent.value = true))
    .catch((err) => alertsStore.catchError(err))
    .finally(() => (isSendingApplication.value = false));
}

function close() {
  modalRef.value?.close();
}

defineExpose({ modalRef });
</script>

<style scoped lang="scss">
.actions > * {
  width: 100%;
}
</style>
